/**
 * useAuth hook - Updated for username/password authentication
 */

import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/auth';
import { getAPIClient } from '../api/client';
import { useToastStore } from '../stores/toast';
import { login as loginAPI, register as registerAPI, logout as logoutAPI, refreshToken as refreshTokenAPI } from '../api/auth';
import { getCurrentUserProfile } from '../api/users';
import { getBaseUrl } from '../utils/env';
import type { LoginRequest, RegisterRequest } from '../api/auth';
import { useWorkspaceStore } from '../stores/workspace';

export function useAuth() {
  const { 
    apiKey, 
    accessToken, 
    refreshToken,
    user,
    username, 
    isAuthenticated, 
    setApiKey, 
    setAccessToken,
    setUser,
    setUsername, 
    logout 
  } = useAuthStore();
  const { addToast } = useToastStore();
  const [isValidating, setIsValidating] = useState(false);

  // Initialize API client with token
  useEffect(() => {
    const client = getAPIClient();

    const devMasterKey =
      typeof import.meta.env.VITE_MASTER_API_KEY === 'string' ? import.meta.env.VITE_MASTER_API_KEY : '';
    const useDevMasterKey =
      import.meta.env.DEV &&
      devMasterKey.length > 0 &&
      apiKey === devMasterKey;

    // Dev: VITE_MASTER_API_KEY wins over persisted JWT (same client slot; stale OAuth tokens would 401)
    if (useDevMasterKey) {
      client.setApiKey(apiKey);
      console.log('[Auth] Dev: API client uses VITE_MASTER_API_KEY (not persisted JWT)');
    } else if (accessToken) {
      client.setAccessToken(accessToken);
      console.log('[Auth] JWT token set on client');
    } else if (apiKey) {
      client.setApiKey(apiKey);
      console.log('[Auth] API key set on client:', apiKey.substring(0, 10) + '...');
    } else {
      // Only clear auth if we explicitly don't have a token
      // Don't clear if token was set in main.tsx before zustand loaded
      // The token will be cleared on logout anyway
      if (client.hasAuth()) {
        console.log('[Auth] Store has no token, but API client has one - keeping it (may be from main.tsx)');
      } else {
        client.clearAuth();
      }
    }
  }, [apiKey, accessToken]);

  // Dev master key: store has no `user`, so App treats you as logged out (Keystone shows landing at /).
  // Resolve profile from GET /users/me (master key maps to configured dev user email on the API) so UI shows as signed in.
  useEffect(() => {
    const devMasterKey =
      typeof import.meta.env.VITE_MASTER_API_KEY === 'string' ? import.meta.env.VITE_MASTER_API_KEY : '';
    const isDevMaster =
      import.meta.env.DEV &&
      devMasterKey.length > 0 &&
      apiKey === devMasterKey &&
      !accessToken;

    if (!isDevMaster || user) return;

    let cancelled = false;
    void (async () => {
      try {
        const profile = await getCurrentUserProfile();
        if (cancelled) return;
        setUser({
          id: profile.id,
          email: profile.email,
          username: profile.username,
          full_name: profile.full_name,
          is_email_verified: profile.is_email_verified,
          tenant_id: profile.tenant_assignments?.[0]?.tenant_id,
        });
        console.log('[Auth] Dev master key: loaded user from GET /users/me');
      } catch (e) {
        if (cancelled) return;
        console.warn('[Auth] Dev master key: GET /users/me failed; using placeholder user for UI', e);
        setUser({
          id: '00000000-0000-4000-8000-000000000001',
          email: 'dev@local.invalid',
          username: 'dev',
          is_email_verified: true,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiKey, accessToken, user, setUser]);

  // Auto-refresh token before expiration
  useEffect(() => {
    if (!refreshToken || !accessToken) return;

    const tokenExpiry = JSON.parse(atob(accessToken.split('.')[1])).exp * 1000;
    const timeUntilExpiry = tokenExpiry - Date.now();
    const refreshTime = timeUntilExpiry - 60000; // Refresh 1 minute before expiry

    if (refreshTime > 0) {
      const timer = setTimeout(async () => {
        try {
          const response = await refreshTokenAPI({ refresh_token: refreshToken });
          setAccessToken(response.access_token, refreshToken, user || undefined);
          addToast({
            message: 'Session refreshed',
            type: 'success',
          });
        } catch (error) {
          console.error('[Auth] Token refresh failed:', error);
          logout();
          addToast({
            message: 'Session expired. Please log in again.',
            type: 'error',
          });
        }
      }, refreshTime);

      return () => clearTimeout(timer);
    }
  }, [accessToken, refreshToken, user, setAccessToken, logout, addToast]);

  const login = async (request: LoginRequest): Promise<any> => {
    console.log('[Auth] Login called for:', request.username);
    
    setIsValidating(true);
    try {
      const response = await loginAPI(request);
      
      console.log('[Auth] Login response:', {
        has_access_token: !!response.access_token,
        requires_password_reset: response.requires_password_reset,
        user_email: response.user?.email,
      });
      
      // Store tokens and user info
      setAccessToken(response.access_token, response.refresh_token, response.user);
      
      // Set API client token
      const client = getAPIClient();
      client.setAccessToken(response.access_token);
      
      // Check if password reset is required (e.g., default password)
      if (response.requires_password_reset) {
        console.log('[Auth] Password reset required - redirecting to reset page');
        // Redirect to reset password using base URL so it works in all deployments:
        // - Local dev: http://localhost:5176/reset-password?forced=true
        // - On-prem at root: http://10.0.245.31/reset-password?forced=true
        // - On-prem with /keystone-rfp/ base: http://10.0.245.31/keystone-rfp/reset-password?forced=true
        const baseUrl = getBaseUrl();
        const resetPath = baseUrl === '/' 
          ? '/reset-password?forced=true' 
          : baseUrl.replace(/\/$/, '') + '/reset-password?forced=true';
        const fullUrl = window.location.origin + resetPath;
        console.log('[Auth] Redirecting to:', fullUrl);
        setTimeout(() => {
          window.location.href = fullUrl;
        }, 0);
        // Return response so caller can check the flag
        return response;
      }
      
      console.log('[Auth] No password reset required - proceeding with normal login');
      addToast({
        message: 'Successfully signed in!',
        type: 'success',
      });

      // Clear dev mock role so a new session never inherits a stale localStorage role
      // that would bypass GET /rbac/permissions/me (see useRoleSwitch).
      try {
        const { clearMockRoleOnLogout } = await import('./useRoleSwitch');
        clearMockRoleOnLogout();
      } catch (e) {
        console.warn('[Auth] Failed to clear mock role after login:', e);
      }
      
      return response;
    } catch (error: any) {
      console.error('[Auth] Login failed:', error);
      const message = error.response?.data?.detail || error.message || 'Login failed. Please check your credentials.';
      addToast({
        message,
        type: 'error',
      });
      throw error;
    } finally {
      setIsValidating(false);
    }
  };

  const register = async (request: RegisterRequest) => {
    console.log('[Auth] Register called for:', request.email);
    
    setIsValidating(true);
    try {
      const response = await registerAPI(request);
      
      addToast({
        message: response.message || 'Registration successful! Please verify your email.',
        type: 'success',
      });
      
      return response;
    } catch (error: any) {
      console.error('[Auth] Registration failed:', error);
      const message = error.response?.data?.detail || error.message || 'Registration failed. Please try again.';
      addToast({
        message,
        type: 'error',
      });
      throw error;
    } finally {
      setIsValidating(false);
    }
  };

  const handleLogout = async () => {
    try {
      if (accessToken) {
        await logoutAPI();
      }
    } catch (error) {
      console.error('[Auth] Logout API call failed:', error);
    } finally {
      // Clear local state regardless of API call result
      logout();
      const client = getAPIClient();
      client.clearAuth();
      client.setTenantId(null);
      // Drop persisted workspace so hooks do not call /rbac/permissions/me or /projects/:id without a token
      useWorkspaceStore.getState().clearWorkspace();
      
      // Clear mock role on logout
      try {
        const { clearMockRoleOnLogout } = await import('./useRoleSwitch');
        clearMockRoleOnLogout();
      } catch (error) {
        console.warn('[Auth] Failed to clear mock role on logout:', error);
      }
      
      addToast({
        message: 'Logged out successfully',
        type: 'success',
      });
      
      // Redirect to login page using base URL so it works in all deployments:
      // - Local dev: http://localhost:5176/login?logout=true
      // - On-prem at root: http://10.0.245.31/login?logout=true
      // - On-prem with /keystone-rfp/ base: http://10.0.245.31/keystone-rfp/login?logout=true
      const baseUrl = getBaseUrl();
      const loginPath = baseUrl === '/' 
        ? '/login?logout=true' 
        : baseUrl.replace(/\/$/, '') + '/login?logout=true';
      const fullUrl = window.location.origin + loginPath;
      console.log('[Auth] Logout redirect to:', fullUrl);
      window.location.href = fullUrl;
    }
  };

  return {
    apiKey,
    accessToken,
    user,
    username,
    isAuthenticated,
    login,
    register,
    logout: handleLogout,
    isValidating,
  };
}
